import { useEffect, useRef, useState } from 'react';
import { Modal, BlockStack, InlineStack, Text, Checkbox, Badge, Banner, Button, ButtonGroup, Spinner, Box, Divider, ProgressBar } from '@shopify/polaris';
import { formatToCurrency } from '../../../shared/components/functions.jsx';
import CompanySelect from '../../../shared/components/companySelect';

const PRICE_CHOICES = { ORDER: 'order', CURRENT: 'current' };
const FILE_COPY_POLL_INTERVAL_MS = 1500;
const FILE_COPY_MAX_POLL_FAILURES = 3;

const getChoiceKey = (difference) => `${difference.BestellingLijnID}:${difference.field}`;
const formatDate = (value) => (value ? String(value).substring(0, 10) : '');
const foreignAmountFormatter = new Intl.NumberFormat('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// prices are euro unless the line / product carries another currency, then show the amount with that currency instead of a € sign
const formatPrice = (value, currency) => (currency && currency.toUpperCase() !== 'EUR' ? `${foreignAmountFormatter.format(value)} ${currency.toUpperCase()}` : formatToCurrency(value));
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/*
  Duplicates a saved purchase order into a new draft.
  The preview endpoint tells which attachments can be copied and which line prices drifted from the
  product data; the user picks the supplier (the same one by default, or another one), the files and the
  prices, then the server creates the copy.
  The attachments are copied by a background job on the server (one SharePoint round trip per file
  takes too long for a single request), so after the order is created this modal polls the job and
  shows the progress until every file is done.
*/
export default function DuplicatePurchaseModal({ open, onClose, purchaseId, purchaseNumber, onDuplicated }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [priceChoices, setPriceChoices] = useState({});
  const [supplier, setSupplier] = useState(null); // { id, name } of the supplier the copy is addressed to
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [created, setCreated] = useState(null); // response of the duplicate call: the new order exists from here on
  const [copyJob, setCopyJob] = useState(null); // latest snapshot of the background file copy
  const [copyJobError, setCopyJobError] = useState('');
  const [result, setResult] = useState(null); // final state when some files could not be copied
  const closedRef = useRef(false); // stops the polling loop once the modal is closed or left

  useEffect(() => {
    if (!open || !purchaseId || purchaseId === 'new') return undefined;
    let cancelled = false;
    closedRef.current = false;

    const loadPreview = async () => {
      setLoading(true);
      setErrorMessage('');
      setResult(null);
      setCreated(null);
      setCopyJob(null);
      setCopyJobError('');
      setPreview(null);
      setSupplier(null);

      try {
        const response = await fetch(`/api/purchases/${purchaseId}/duplicate-preview`);
        const data = await response.json();
        if (!response.ok) throw new Error(data?.msg || 'Failed to load the duplicate preview.');
        if (cancelled) return;

        setPreview(data);
        setSupplier({ id: data.purchase?.LeverancierID ?? null, name: data.purchase?.supplierName || '' });
        // the supplier confirmation belongs to the original order, so it starts unselected
        setSelectedDocumentIds((data.documents || []).filter((doc) => !doc.isSupplierConfirmation).map((doc) => doc.DocumentID));
        setPriceChoices(Object.fromEntries((data.priceDifferences || []).map((difference) => [getChoiceKey(difference), PRICE_CHOICES.ORDER])));
      } catch (error) {
        if (!cancelled) setErrorMessage(error.message || 'Failed to load the duplicate preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
      closedRef.current = true;
    };
  }, [open, purchaseId]);

  const documents = preview?.documents || [];
  const priceDifferences = preview?.priceDifferences || [];
  const sourceSupplierId = preview?.purchase?.LeverancierID ?? null;
  const isOtherSupplier = Boolean(supplier?.id) && String(supplier.id) !== String(sourceSupplierId);
  const currentPriceCount = Object.values(priceChoices).filter((choice) => choice === PRICE_CHOICES.CURRENT).length;

  const toggleDocument = (documentId, checked) => {
    setSelectedDocumentIds((current) => (checked ? [...new Set([...current, documentId])] : current.filter((id) => id !== documentId)));
  };

  const setAllDocuments = (checked) => setSelectedDocumentIds(checked ? documents.map((doc) => doc.DocumentID) : []);

  const setPriceChoice = (difference, use) => setPriceChoices((current) => ({ ...current, [getChoiceKey(difference)]: use }));

  const setAllPriceChoices = (use) => setPriceChoices(Object.fromEntries(priceDifferences.map((difference) => [getChoiceKey(difference), use])));

  const leaveForNewPurchase = (data) => {
    closedRef.current = true;
    onDuplicated(data);
  };

  const handleClose = () => {
    closedRef.current = true;
    onClose();
  };

  const finishWithJob = (data, job) => {
    if (closedRef.current) return;
    if (Array.isArray(job.warnings) && job.warnings.length > 0) {
      // the order exists, but not every file made it: show that before leaving this page
      setResult({ ...data, copiedDocuments: job.copied || [], documentWarnings: job.warnings });
    } else {
      leaveForNewPurchase(data);
    }
  };

  const pollFileCopy = async (data, jobId) => {
    let failures = 0;

    while (!closedRef.current) {
      await sleep(FILE_COPY_POLL_INTERVAL_MS);
      if (closedRef.current) return;

      try {
        const response = await fetch(`/api/purchases/duplicate-jobs/${jobId}`);
        const snapshot = await response.json();
        if (response.status === 404) {
          // the job is gone (server restarted or the job expired): the files that were copied are on the order
          if (!closedRef.current) setCopyJobError(snapshot?.msg || 'The file copy job is no longer available.');
          return;
        }
        if (!response.ok) throw new Error(snapshot?.msg || 'Could not read the file copy progress.');
        if (closedRef.current) return;

        failures = 0;
        setCopyJob(snapshot);
        if (snapshot.status === 'done') {
          finishWithJob(data, snapshot);
          return;
        }
      } catch (error) {
        failures += 1;
        if (failures >= FILE_COPY_MAX_POLL_FAILURES) {
          if (!closedRef.current) setCopyJobError(error.message || 'Could not read the file copy progress.');
          return;
        }
      }
    }
  };

  const duplicate = async () => {
    setSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`/api/purchases/${purchaseId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplierId: isOtherSupplier ? supplier.id : undefined,
          documentIds: selectedDocumentIds,
          priceChoices: priceDifferences.map((difference) => ({
            BestellingLijnID: difference.BestellingLijnID,
            field: difference.field,
            use: priceChoices[getChoiceKey(difference)] || PRICE_CHOICES.ORDER
          }))
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.msg || 'Failed to duplicate the purchase order.');

      if (!data.fileCopy) {
        leaveForNewPurchase(data);
        return;
      }

      setCreated(data);
      setCopyJob(data.fileCopy);
      if (data.fileCopy.status === 'done') {
        finishWithJob(data, data.fileCopy);
        return;
      }
      pollFileCopy(data, data.fileCopy.jobId);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to duplicate the purchase order.');
    } finally {
      setSubmitting(false);
    }
  };

  const createdNumber = created?.purchase?.Bestellingnummer || result?.purchase?.Bestellingnummer || '';
  const primaryAction = result
    ? { content: `Open purchase ${createdNumber}`.trim(), onAction: () => leaveForNewPurchase(result) }
    : created
    ? { content: `Open purchase ${createdNumber}`.trim(), onAction: () => leaveForNewPurchase(created) }
    : { content: 'Duplicate', onAction: duplicate, loading: submitting, disabled: loading || !preview || submitting || !supplier?.id };

  const renderDocuments = () => {
    if (documents.length === 0) {
      return (
        <Text as="p" tone="subdued">
          This purchase order has no attached files. The generated purchase PDF is never copied.
        </Text>
      );
    }

    return (
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" tone="subdued">
            {selectedDocumentIds.length} of {documents.length} file(s) selected. The generated purchase PDF is never copied.
          </Text>
          <ButtonGroup>
            <Button size="slim" onClick={() => setAllDocuments(true)} disabled={submitting}>
              Select all
            </Button>
            <Button size="slim" onClick={() => setAllDocuments(false)} disabled={submitting}>
              Select none
            </Button>
          </ButtonGroup>
        </InlineStack>
        <BlockStack gap="200">
          {documents.map((doc) => (
            <InlineStack key={doc.DocumentID} gap="200" blockAlign="center" wrap={false}>
              <Checkbox
                id={`duplicate-document-${doc.DocumentID}`}
                label={
                  <InlineStack gap="200" blockAlign="center">
                    <span>{doc.Filename}</span>
                    {doc.Datum ? (
                      <Text as="span" tone="subdued">
                        {formatDate(doc.Datum)}
                      </Text>
                    ) : null}
                    {doc.isSupplierConfirmation ? <Badge tone="attention">Supplier confirmation</Badge> : null}
                  </InlineStack>
                }
                checked={selectedDocumentIds.includes(doc.DocumentID)}
                onChange={(checked) => toggleDocument(doc.DocumentID, checked)}
                disabled={submitting}
              />
            </InlineStack>
          ))}
        </BlockStack>
        {documents.some((doc) => doc.isSupplierConfirmation) ? (
          <Text as="p" tone="subdued">
            Supplier confirmations belong to the original order. When copied they become ordinary attachments and do not set a confirmed delivery date on the new order.
          </Text>
        ) : null}
      </BlockStack>
    );
  };

  const renderPrices = () => {
    if (priceDifferences.length === 0) {
      return (
        <Text as="p" tone="subdued">
          All prices on this order match the current product prices, so they are copied as they are.
        </Text>
      );
    }

    return (
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p">
            {priceDifferences.length} price(s) differ from the current product data. Choose which price the new order should use ({currentPriceCount} set to current).
          </Text>
          <ButtonGroup>
            <Button size="slim" onClick={() => setAllPriceChoices(PRICE_CHOICES.ORDER)} disabled={submitting}>
              Keep all order prices
            </Button>
            <Button size="slim" onClick={() => setAllPriceChoices(PRICE_CHOICES.CURRENT)} disabled={submitting}>
              Use all current prices
            </Button>
          </ButtonGroup>
        </InlineStack>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }} data-testid="duplicate-price-table">
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #d2d5d8' }}>
                <th style={{ padding: '6px 8px' }}>Code</th>
                <th style={{ padding: '6px 8px' }}>Description</th>
                <th style={{ padding: '6px 8px' }}>Price</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>On this order</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Current product price</th>
                <th style={{ padding: '6px 8px' }}>Use</th>
              </tr>
            </thead>
            <tbody>
              {priceDifferences.map((difference) => {
                const key = getChoiceKey(difference);
                const choice = priceChoices[key] || PRICE_CHOICES.ORDER;
                return (
                  <tr key={key} style={{ borderBottom: '1px solid #e3e5e7' }}>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      {difference.produktnr}
                      {difference.Productref ? (
                        <Text as="span" tone="subdued">
                          {' '}
                          {difference.Productref}
                        </Text>
                      ) : null}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{difference.tekstlijn}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      {difference.fieldLabel} ({difference.field})
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatPrice(difference.orderPrice, difference.lineCurrency)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {formatPrice(difference.currentPrice, difference.currentPriceCurrency)}
                      {difference.currentPriceDate ? (
                        <Text as="span" tone="subdued">
                          {' '}
                          ({formatDate(difference.currentPriceDate)})
                        </Text>
                      ) : null}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      <ButtonGroup variant="segmented">
                        <Button size="slim" pressed={choice === PRICE_CHOICES.ORDER} onClick={() => setPriceChoice(difference, PRICE_CHOICES.ORDER)} disabled={submitting}>
                          Order
                        </Button>
                        <Button size="slim" pressed={choice === PRICE_CHOICES.CURRENT} onClick={() => setPriceChoice(difference, PRICE_CHOICES.CURRENT)} disabled={submitting}>
                          Current
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </BlockStack>
    );
  };

  const renderCopyProgress = () => {
    const total = copyJob?.total || 0;
    const processed = Math.min(total, copyJob?.processed || 0);
    const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
    const finished = copyJob?.status === 'done';
    const partialWarnings = Array.isArray(copyJob?.warnings) ? copyJob.warnings : [];

    return (
      <BlockStack gap="300">
        <Banner tone="success" title={`Purchase order ${createdNumber} was created`}>
          <p>{created.lines?.length || 0} line(s) copied. The attached files are being copied in the background.</p>
        </Banner>
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            {!finished && !copyJobError ? <Spinner size="small" /> : null}
            <span data-testid="duplicate-copy-progress">
              <Text as="span">
                Copying files: {processed} of {total}
                {copyJob?.currentFilename ? ` · ${copyJob.currentFilename}` : ''}
              </Text>
            </span>
          </InlineStack>
          <ProgressBar progress={progressPercent} size="small" />
        </BlockStack>
        {partialWarnings.length > 0 ? (
          <Banner tone="warning" title="Some files could not be copied">
            <BlockStack gap="100">
              {partialWarnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </BlockStack>
          </Banner>
        ) : null}
        {copyJobError ? (
          <Banner tone="warning" title="The copy progress can no longer be followed">
            <p>{copyJobError}</p>
            <p>The server keeps copying the files; they appear on the new purchase order once finished.</p>
          </Banner>
        ) : (
          <Text as="p" tone="subdued">
            You can open the new purchase order right away. Files that are still being copied appear there once they are finished.
          </Text>
        )}
      </BlockStack>
    );
  };

  const renderResult = () => (
    <BlockStack gap="300">
      <Banner tone="success" title={`Purchase order ${createdNumber} was created`}>
        <p>
          {result.lines?.length || 0} line(s) copied, {result.copiedDocuments?.length || 0} file(s) copied.
        </p>
      </Banner>
      <Banner tone="warning" title="Some files could not be copied">
        <BlockStack gap="100">
          {result.documentWarnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </BlockStack>
      </Banner>
    </BlockStack>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Duplicate purchase order ${purchaseNumber || ''}`.trim()}
      size="large"
      primaryAction={primaryAction}
      secondaryActions={[{ content: created || result ? 'Close' : 'Cancel', onAction: handleClose, disabled: submitting }]}
    >
      <Modal.Section>
        {loading ? (
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" />
            <Text as="span">Comparing prices and collecting attached files...</Text>
          </InlineStack>
        ) : null}

        {errorMessage ? (
          <Box paddingBlockEnd="300">
            <Banner tone="critical" title="Something went wrong">
              <p>{errorMessage}</p>
            </Banner>
          </Box>
        ) : null}

        {result ? renderResult() : null}

        {created && !result ? renderCopyProgress() : null}

        {!loading && !result && !created && preview ? (
          <BlockStack gap="400">
            <Banner tone="info">
              <p>
                A new draft purchase order for {supplier?.name || preview.purchase?.supplierName || 'this supplier'} will be created with {preview.lineCount} line(s). Number, date, status, owner,
                delivery dates and the confirmed delivery date start fresh; received quantities start at zero.
              </p>
            </Banner>

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Supplier
              </Text>
              <div data-testid="duplicate-supplier-select">
                <CompanySelect
                  id="LeverancierID"
                  label="Duplicate to supplier"
                  value={supplier?.id}
                  isDisabled={submitting}
                  onChange={(value, _id, label) => setSupplier(value ? { id: value, name: String(label || '').replace(/\s*\(.*\)\s*$/, '') } : null)}
                />
              </div>
              {isOtherSupplier ? (
                <Text as="p" tone="subdued">
                  The copy goes to another supplier than {preview.purchase?.supplierName || 'the original one'}: it gets that supplier&apos;s language and is no longer linked to the inquiries of the
                  original order. Check the purchase prices, they are the prices of the original supplier.
                </Text>
              ) : null}
            </BlockStack>

            <Divider />

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Attached files
              </Text>
              {renderDocuments()}
            </BlockStack>

            <Divider />

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Prices
              </Text>
              {renderPrices()}
            </BlockStack>
          </BlockStack>
        ) : null}
      </Modal.Section>
    </Modal>
  );
}
